import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

/**
 * Collapsible — clickable header reveals/hides children.
 * Used for the right-side dashboard panels (Insights / Resume Makeover).
 *
 * Props
 *   title         visible big text
 *   eyebrow       small uppercase tag above title
 *   accent        accent color (used when headerBg/eyebrowColor not provided)
 *   headerBg      optional gradient or solid header background
 *   eyebrowColor  optional eyebrow color override
 *   titleColor    title color (default "white")
 *   defaultOpen   boolean (default true)
 *   testid        root data-testid; also generates `${testid}-toggle` and `-body`
 */
export default function Collapsible({
  title,
  eyebrow,
  accent = "#FF6A00",
  headerBg,
  eyebrowColor,
  titleColor = "white",
  defaultOpen = true,
  testid,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 px-3 py-2.5 mb-2 transition-colors"
        style={{
          background: headerBg || (open ? "#0F1B2D" : "rgba(15,27,45,0.92)"),
          border: `2px solid #0F1B2D`,
          borderRadius: 8,
          boxShadow: "3px 3px 0 0 #0F1B2D",
          cursor: "pointer",
        }}
        data-testid={`${testid}-toggle`}
        aria-expanded={open}
      >
        <div className="flex-1 text-left min-w-0">
          <div className="font-mono-rm uppercase text-[9px] tracking-widest truncate"
               style={{ color: eyebrowColor || accent, fontWeight: 700 }}>
            {eyebrow}
          </div>
          <div className="font-display uppercase text-[13px] leading-tight truncate"
               style={{ color: titleColor, letterSpacing: "-0.01em" }}>
            {title}
          </div>
        </div>
        <div style={{ color: eyebrowColor || accent }}>
          {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </button>
      {open && <div data-testid={`${testid}-body`}>{children}</div>}
    </div>
  );
}
